'use client'

import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui'
import { cn } from '@repo/ui'
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Lightbulb,
  MessageSquare,
  Play,
  Rocket,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  type PromptAnalysis,
  type PromptVersion,
  useApplyVersion,
  useDefinitionSuggestions,
  useOptimizePrompt,
  useOptimizerQuota,
  usePromptAnalyses,
  usePromptVersions,
  useRollbackVersion,
  useRunAnalysis,
} from '@/hooks/use-agent-optimizer'
import { useAuth } from '@/hooks/use-auth'

interface OptimizerTabProps {
  agentId: string
  channels: { id: string; name: string }[]
}

function apiErrorMessage(err: unknown): string | null {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message
  return typeof message === 'string' ? message : null
}

function pluralizeConversas(count: number, singular: string, plural: string) {
  return count === 1 ? `1 conversa ${singular}` : `${count} conversas ${plural}`
}

type DiffLine = { type: 'same' | 'added' | 'removed'; text: string }

/** Diff de linhas via LCS — suficiente para comparar versões de prompt. */
function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')
  const n = a.length
  const m = b.length
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0)
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] =
        a[i] === b[j]
          ? dp[i + 1]![j + 1]! + 1
          : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!)
    }
  }
  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      result.push({ type: 'same', text: a[i]! })
      i++
      j++
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      result.push({ type: 'removed', text: a[i]! })
      i++
    } else {
      result.push({ type: 'added', text: b[j]! })
      j++
    }
  }
  while (i < n) result.push({ type: 'removed', text: a[i++]! })
  while (j < m) result.push({ type: 'added', text: b[j++]! })
  return result
}

const DIFF_CONTEXT_LINES = 3

function PromptDiff({ before, after }: { before: string; after: string }) {
  const rendered = useMemo(() => {
    const lines = diffLines(before, after)
    const out: Array<DiffLine | { type: 'skip'; count: number }> = []
    let sameRun: DiffLine[] = []

    const flushRun = (isEnd: boolean) => {
      const keepStart = out.length === 0 ? 0 : DIFF_CONTEXT_LINES
      const keepEnd = isEnd ? 0 : DIFF_CONTEXT_LINES
      if (sameRun.length > keepStart + keepEnd + 1) {
        out.push(...sameRun.slice(0, keepStart))
        out.push({ type: 'skip', count: sameRun.length - keepStart - keepEnd })
        if (keepEnd > 0) out.push(...sameRun.slice(-keepEnd))
      } else {
        out.push(...sameRun)
      }
      sameRun = []
    }

    for (const line of lines) {
      if (line.type === 'same') {
        sameRun.push(line)
      } else {
        flushRun(false)
        out.push(line)
      }
    }
    flushRun(true)
    return out
  }, [before, after])

  return (
    <div className="border-border bg-muted max-h-96 overflow-auto rounded-md border p-3 font-mono text-xs leading-relaxed">
      {rendered.map((line, i) =>
        line.type === 'skip' ? (
          <div key={i} className="text-muted-foreground py-1 italic">
            ⋯ {line.count} linhas sem alteração
          </div>
        ) : (
          <div
            key={i}
            className={cn(
              'whitespace-pre-wrap',
              line.type === 'added' && 'bg-success/10 text-success',
              line.type === 'removed' &&
                'bg-destructive/10 text-destructive line-through decoration-1'
            )}
          >
            <span className="mr-1 select-none">
              {line.type === 'added'
                ? '+'
                : line.type === 'removed'
                  ? '−'
                  : ' '}
            </span>
            {line.text || ' '}
          </div>
        )
      )}
    </div>
  )
}

function InsightBlock({
  icon,
  title,
  items,
  colorClass,
  fullWidth,
}: {
  icon: React.ReactNode
  title: string
  items: string[]
  colorClass: string
  fullWidth?: boolean
}) {
  return (
    <div
      className={cn(
        'border-border rounded-lg border p-4',
        fullWidth && 'md:col-span-2'
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <div
          className={cn(
            'flex size-7 items-center justify-center rounded-md',
            colorClass
          )}
        >
          {icon}
        </div>
        <span className="text-foreground text-xs font-semibold">{title}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-muted-foreground text-xs italic">
          Sem itens identificados.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map((item, i) => (
            <li
              key={i}
              className="text-foreground flex items-start gap-2 text-xs leading-relaxed"
            >
              <span className="text-muted-foreground mt-0.5 shrink-0">•</span>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function VersionCard({
  version,
  orgId,
  agentId,
}: {
  version: PromptVersion
  orgId: string
  agentId: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [view, setView] = useState<'diff' | 'prompt'>('diff')
  const applyVersion = useApplyVersion(orgId, agentId)
  const rollbackVersion = useRollbackVersion(orgId, agentId)

  function handleCopy() {
    void navigator.clipboard.writeText(version.optimizedPrompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleApply() {
    try {
      await applyVersion.mutateAsync(version.id)
      toast.success('Prompt aplicado com sucesso')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Erro ao aplicar versão')
    }
  }

  async function handleRollback() {
    try {
      await rollbackVersion.mutateAsync(version.id)
      toast.success('Prompt anterior restaurado')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Erro ao reverter versão')
    }
  }

  const isApplied = !!version.appliedAt

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="hover:bg-muted/40 flex w-full items-center justify-between px-4 py-3 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-foreground text-xs font-semibold">
              {new Date(version.createdAt).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {isApplied && (
            <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 text-xs">
              Aplicado
            </Badge>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="text-muted-foreground size-4" />
        ) : (
          <ChevronDown className="text-muted-foreground size-4" />
        )}
      </button>

      {expanded && (
        <div className="border-border flex flex-col gap-3 border-t px-4 pt-3 pb-4">
          {version.changeNotes && (
            <div className="flex flex-col gap-1.5">
              <Label className="text-muted-foreground text-xs font-medium">
                Principais mudanças
              </Label>
              <Textarea
                value={version.changeNotes}
                readOnly
                rows={3}
                className="bg-muted text-foreground resize-none text-xs"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground text-xs font-medium">
                {view === 'diff'
                  ? 'Diferenças em relação ao prompt anterior'
                  : 'Prompt otimizado'}
              </Label>
              <button
                type="button"
                onClick={() =>
                  setView((p) => (p === 'diff' ? 'prompt' : 'diff'))
                }
                className="text-primary text-xs font-medium hover:underline"
              >
                {view === 'diff' ? 'Ver prompt completo' : 'Ver diferenças'}
              </button>
            </div>
            {view === 'diff' ? (
              <PromptDiff
                before={version.sourcePrompt}
                after={version.optimizedPrompt}
              />
            ) : (
              <Textarea
                value={version.optimizedPrompt}
                readOnly
                rows={14}
                className="bg-muted text-foreground resize-none text-xs leading-relaxed"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="size-3.5" />
              ) : (
                <Copy className="size-3.5" />
              )}
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
            {!isApplied && (
              <Button
                type="button"
                size="sm"
                onClick={() => void handleApply()}
                disabled={applyVersion.isPending}
              >
                Aplicar
              </Button>
            )}
            {isApplied && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void handleRollback()}
                disabled={rollbackVersion.isPending}
              >
                Reverter para o prompt anterior
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function OptimizerTab({ agentId, channels }: OptimizerTabProps) {
  const { currentOrgId } = useAuth()
  const [selectedChannel, setSelectedChannel] = useState('__all__')
  const [positiveText, setPositiveText] = useState('')
  const [negativeText, setNegativeText] = useState('')
  const [autoFillSource, setAutoFillSource] = useState<
    'previous' | 'ai' | null
  >(null)
  const userEditedRef = useRef(false)

  const { data: analyses = [], isFetched: analysesFetched } = usePromptAnalyses(
    currentOrgId,
    agentId
  )
  const runAnalysis = useRunAnalysis(currentOrgId, agentId)
  const optimizePrompt = useOptimizePrompt(currentOrgId, agentId)

  const latestAnalysis: PromptAnalysis | undefined = analyses[0]
  const analysisProcessing = latestAnalysis?.status === 'PROCESSING'

  // Após concluir a análise, o servidor gera a versão otimizada em background;
  // mantém o polling de versões nessa janela (até 5 min pós-conclusão).
  const completedRecently =
    latestAnalysis?.status === 'COMPLETED' &&
    !!latestAnalysis.completedAt &&
    Date.now() - new Date(latestAnalysis.completedAt).getTime() < 5 * 60_000

  const { data: versions = [] } = usePromptVersions(currentOrgId, agentId, {
    refetchInterval:
      analysisProcessing || completedRecently || optimizePrompt.isPending
        ? 4_000
        : false,
  })
  const { data: quota } = useOptimizerQuota(currentOrgId, agentId, {
    refetchInterval: analysisProcessing ? 4_000 : false,
  })

  const latestVersion: PromptVersion | undefined = versions[0]
  const versionForLatest = latestAnalysis
    ? versions.some((v) => v.analysisId === latestAnalysis.id)
    : false
  const awaitingVersion =
    latestAnalysis?.status === 'COMPLETED' && !versionForLatest
  const optimizingVersion =
    optimizePrompt.isPending || (awaitingVersion && completedRecently)

  // Auto-preenchimento das definições: última análise > sugestão da IA.
  const previousDefinitions = analyses.find(
    (a) => a.qualifiedDefinition?.trim() || a.unqualifiedDefinition?.trim()
  )
  const needSuggestions =
    analysesFetched && !previousDefinitions && !userEditedRef.current
  const { data: suggestions, isLoading: suggestionsLoading } =
    useDefinitionSuggestions(currentOrgId, agentId, needSuggestions)

  useEffect(() => {
    if (autoFillSource || userEditedRef.current) return
    const source = previousDefinitions
      ? {
          qualified: previousDefinitions.qualifiedDefinition ?? '',
          unqualified: previousDefinitions.unqualifiedDefinition ?? '',
          from: 'previous' as const,
        }
      : suggestions
        ? {
            qualified: suggestions.qualifiedDefinition,
            unqualified: suggestions.unqualifiedDefinition,
            from: 'ai' as const,
          }
        : null
    if (!source) return
    setPositiveText((current) => current || source.qualified)
    setNegativeText((current) => current || source.unqualified)
    setAutoFillSource(source.from)
  }, [previousDefinitions, suggestions, autoFillSource])

  async function handleRunAnalysis() {
    try {
      await runAnalysis.mutateAsync({
        channelId: selectedChannel === '__all__' ? null : selectedChannel,
        qualifiedDefinition: positiveText || undefined,
        unqualifiedDefinition: negativeText || undefined,
      })
      toast.success(
        'Análise iniciada — você pode sair da página, o processo continua no servidor'
      )
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Erro ao iniciar análise')
    }
  }

  async function handleGenerateVersion() {
    if (!latestAnalysis) return
    try {
      await optimizePrompt.mutateAsync(latestAnalysis.id)
      toast.success('Prompt otimizado gerado')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Erro ao gerar prompt otimizado')
    }
  }

  const isRunning = runAnalysis.isPending || analysisProcessing
  const isBlocked = quota ? !quota.canRun : false

  return (
    <div className="flex flex-col gap-4">
      <section className="border-border bg-card rounded-lg border p-4">
        <div className="mb-4">
          <h3 className="text-foreground text-sm font-semibold">
            Configuração da análise
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            A IA irá analisar as últimas 50 conversas encerradas para gerar
            insights e otimizar o prompt.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium">Canal a analisar</Label>
            <Select value={selectedChannel} onValueChange={setSelectedChannel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os canais</SelectItem>
                {channels.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">
                O que é um lead qualificado?
              </Label>
              <Textarea
                value={positiveText}
                onChange={(e) => {
                  userEditedRef.current = true
                  setPositiveText(e.target.value)
                }}
                rows={4}
                placeholder="Descreva o critério positivo de qualificação..."
                className="resize-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">
                O que NÃO é um lead qualificado?
              </Label>
              <Textarea
                value={negativeText}
                onChange={(e) => {
                  userEditedRef.current = true
                  setNegativeText(e.target.value)
                }}
                rows={4}
                placeholder="Descreva o que desqualifica um lead..."
                className="resize-none"
              />
            </div>
          </div>
          {suggestionsLoading && !positiveText && !negativeText && (
            <p className="text-muted-foreground text-xs italic">
              Gerando sugestão de definições a partir do prompt do agente…
            </p>
          )}
          {autoFillSource && (
            <p className="text-muted-foreground text-xs">
              {autoFillSource === 'ai'
                ? 'Preenchido automaticamente a partir do prompt e das regras de qualificação do agente — edite à vontade.'
                : 'Preenchido com as definições usadas na sua última análise — edite à vontade.'}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <div>
              <Button
                type="button"
                onClick={() => void handleRunAnalysis()}
                disabled={isRunning || isBlocked}
                className="gap-2"
              >
                {isRunning ? (
                  <>
                    <span className="border-primary-foreground/30 border-t-primary-foreground size-4 animate-spin rounded-full border-2" />
                    Analisando conversas...
                  </>
                ) : (
                  <>
                    <Play className="size-4" />
                    Rodar análise
                  </>
                )}
              </Button>
            </div>
            {quota && (
              <p className="text-muted-foreground text-xs">
                {quota.usedThisMonth}/{quota.monthlyCap} análises nos últimos 30
                dias
                {quota.blockedReason
                  ? quota.nextAvailableAt
                    ? ` — Próxima análise disponível em ${new Date(
                        quota.nextAvailableAt
                      ).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : ` — ${quota.blockedReason}`
                  : ''}
              </p>
            )}
          </div>
        </div>
      </section>

      {analysisProcessing && (
        <div className="border-border bg-card rounded-lg border p-4">
          <div className="text-foreground flex items-center gap-2 text-sm font-medium">
            <span className="border-primary/30 border-t-primary size-4 animate-spin rounded-full border-2" />
            Análise em andamento
          </div>
          <p className="text-muted-foreground mt-1.5 text-xs">
            As conversas estão sendo classificadas e analisadas. Você pode sair
            desta página — o processo continua no servidor e o resultado
            aparecerá aqui.
          </p>
        </div>
      )}

      {latestAnalysis?.status === 'FAILED' && (
        <div className="border-destructive/30 bg-destructive/5 rounded-lg border p-4">
          <p className="text-destructive text-sm font-medium">
            A última análise falhou
          </p>
          <p className="text-muted-foreground mt-1.5 text-xs">
            {latestAnalysis.errorMessage ??
              'Falha inesperada no processamento.'}{' '}
            Você pode rodar uma nova análise.
          </p>
        </div>
      )}

      {latestAnalysis?.status === 'COMPLETED' && (
        <>
          <section className="border-border bg-card rounded-lg border p-4">
            <div className="mb-4">
              <h3 className="text-foreground text-sm font-semibold">
                Resultado da última análise
              </h3>
            </div>

            <div className="mb-5 flex flex-wrap gap-3">
              <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <Calendar className="size-3.5" />
                {new Date(latestAnalysis.createdAt).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <MessageSquare className="size-3.5" />
                {latestAnalysis.channelId ?? 'Todos os canais'}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-help text-xs">
                    {pluralizeConversas(
                      latestAnalysis.conversationsAnalyzed,
                      'analisada',
                      'analisadas'
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Conversas encerradas com pelo menos 4 mensagens que entraram
                  na amostra da análise.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge className="bg-success/10 text-success border-success/30 hover:bg-success/10 cursor-help text-xs">
                    {pluralizeConversas(
                      latestAnalysis.conversationsEvolved,
                      'evoluiu',
                      'evoluíram'
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Conversas em que o lead avançou rumo ao objetivo do agente
                  (agendamento, qualificação...). Contado a partir da
                  classificação individual de cada conversa, ancorada em
                  registros do sistema.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge className="bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/10 cursor-help text-xs">
                    {pluralizeConversas(
                      latestAnalysis.conversationsStalled,
                      'estagnou',
                      'estagnaram'
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Conversas em que o lead estagnou, desistiu ou parou de
                  responder.
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InsightBlock
                icon={
                  <TrendingUp className="text-success size-4" strokeWidth={2} />
                }
                title="O que está convencendo a agendar"
                items={latestAnalysis.insights.what_is_converting ?? []}
                colorClass="bg-success/10"
              />
              <InsightBlock
                icon={
                  <Target className="text-warning size-4" strokeWidth={2} />
                }
                title="Padrões de leads não qualificados"
                items={latestAnalysis.insights.unqualified_patterns ?? []}
                colorClass="bg-warning/10"
              />
              <InsightBlock
                icon={
                  <TrendingDown
                    className="text-destructive size-4"
                    strokeWidth={2}
                  />
                }
                title="O que NÃO está funcionando"
                items={latestAnalysis.insights.what_is_not_working ?? []}
                colorClass="bg-destructive/10"
              />
              <InsightBlock
                icon={
                  <Lightbulb className="text-info size-4" strokeWidth={2} />
                }
                title="A conversa teria mais resultado se..."
                items={
                  latestAnalysis.insights
                    .conversation_could_have_more_results_if ?? []
                }
                colorClass="bg-info-soft"
              />
              <InsightBlock
                icon={
                  <Rocket className="text-primary size-4" strokeWidth={2} />
                }
                title="O que precisa melhorar no SDR..."
                items={latestAnalysis.insights.general_improvements ?? []}
                colorClass="bg-primary/10"
                fullWidth
              />
            </div>
          </section>

          {optimizingVersion && (
            <div className="border-border bg-card text-muted-foreground flex items-center gap-2 rounded-lg border p-4 text-xs">
              <span className="border-muted-foreground/30 border-t-muted-foreground size-4 animate-spin rounded-full border-2" />
              Gerando prompt otimizado a partir da análise…
            </div>
          )}

          {awaitingVersion && !optimizingVersion && (
            <div className="border-border bg-card flex items-center justify-between gap-3 rounded-lg border p-4">
              <p className="text-muted-foreground text-xs">
                Esta análise ainda não tem um prompt otimizado gerado.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void handleGenerateVersion()}
                disabled={optimizePrompt.isPending}
              >
                Gerar prompt otimizado
              </Button>
            </div>
          )}

          {versions.length > 0 && (
            <>
              <section className="border-border bg-card rounded-lg border p-4">
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-foreground text-sm font-semibold">
                      Prompt aprimorado
                    </h3>
                    {latestVersion?.appliedAt && (
                      <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 text-xs">
                        Aplicado
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    Prompt gerado pela análise mais recente com base nos
                    insights identificados.
                  </p>
                </div>
                {latestVersion && currentOrgId && (
                  <div className="flex flex-col gap-2">
                    {versions.map((v) => (
                      <VersionCard
                        key={v.id}
                        version={v}
                        orgId={currentOrgId}
                        agentId={agentId}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}
    </div>
  )
}
